import React from 'react';
import EmptyState from '../oracle/EmptyState';
import BoneButton from '../oracle/BoneButton';
import CensorBar from '../oracle/CensorBar';
import FeedbackSection from '../home/FeedbackSection';
import { COPY } from '../../contracts/copyMap';

// RECEIVED — what other members said about THIS member's scans.
//
// ── WHY THIS SCREEN HAD TO EXIST ────────────────────────────────────────────
//
// Every feedback_enabled scan sits in the Explore deck independently, and the
// deck orders by responses_count ASC — so an OLD under-read scan is served
// before a newer one. Feedback therefore arrives on any scan, at any time.
//
// Home only ever rendered the LATEST scan's peer_feedback, and getVaultEntry
// does not return the field at all, so feedback on anything else was
// unreachable: the data shipped to the browser inside listVaultEntries and was
// simply never drawn. The "your portrait was read" email pointed at a page
// that could not show what the email described.
//
// ── SEALED SCANS ARE ABSENT, NOT EMPTY ──────────────────────────────────────
//
// listVaultEntries nulls `peer_feedback` on a locked row (there is no verdict
// in a sealed payload and this is part of it), so a sealed scan cannot appear
// here even if members have read it. That is the safe direction — the gate is
// the gate — but it is silent, so the footnote below says so out loud rather
// than letting a member conclude nobody has read them.
//
// The rows reuse FeedbackSection outright. Home and this list must never show
// different numbers or different lock behaviour for the same scan, and one
// component is the only way to guarantee that.
function formatDate(iso) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

/** Entries carrying peer feedback, newest first. */
export function withFeedback(entries) {
  return (entries || []).filter(
    (e) => e && e.peer_feedback && e.peer_feedback.responses_count > 0,
  );
}

/** Is anything in this page still sealed, and therefore withheld from the list? */
export function hasSealed(entries) {
  return (entries || []).some((e) => e && e.locked);
}

export default function VaultReceivedView({
  entries,
  onSelect,
  onLoadMore,
  hasMore,
  loadingMore,
}) {
  const rows = withFeedback(entries);
  const sealed = hasSealed(entries);

  // ── THE EMPTY STATE STILL HAS TO PAGE ─────────────────────────────────────
  //
  // This filters a PAGED list client-side. A member whose first page is twenty
  // recent unread scans, with the one somebody actually read sitting on page
  // two, would otherwise get "No feedback yet" and no control to go further —
  // a dead end reached by clicking a link in an email that told them the
  // opposite. So LOAD MORE renders under the empty state too.
  if (!rows.length) {
    return (
      <div data-testid="vault-received-empty">
        <EmptyState
          headline={COPY.FEEDBACK_RECEIVED_EMPTY_HEADLINE}
          subtitle={sealed ? COPY.FEEDBACK_RECEIVED_SEALED_NOTE : COPY.FEEDBACK_EMPTY_BODY}
        />
        {hasMore && (
          <div className="mt-6 flex justify-center">
            <BoneButton onClick={onLoadMore} disabled={loadingMore}>
              {loadingMore ? COPY.ANALYZING_LABEL : COPY.VAULT_LOAD_MORE_CTA}
            </BoneButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="vault-received">
      <ul className="list-none p-0 m-0 flex flex-col gap-4">
        {rows.map((entry) => (
          <li
            key={entry.scan_id}
            className="flex gap-4 items-start bg-[#161615] border border-hairline-soft rounded-card p-4"
          >
            {/* The thumbnail is the only thing naming WHICH scan a row is
                about, so it is a button, not decoration — the date beside it
                is the heading, not a link target. */}
            <button
              type="button"
              onClick={() => onSelect && onSelect(entry.scan_id)}
              // Not the bare date — that is already the row's heading two
              // nodes away, so a screen reader would announce it twice and
              // neither announcement would say what the control DOES.
              aria-label={`Open the reading from ${formatDate(entry.taken_at || entry.timestamp)}`}
              className="relative w-[64px] h-[85px] shrink-0 overflow-hidden bg-vein border-0 p-0 cursor-pointer"
            >
              {entry.thumbnail_url ? (
                <img
                  src={entry.thumbnail_url}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <CensorBar mode="ANONYMOUS" size="sm" />
                </div>
              )}
            </button>

            <div className="min-w-0 flex-1">
              <FeedbackSection
                scanId={entry.scan_id}
                feedback={entry.peer_feedback}
                heading={formatDate(entry.taken_at || entry.timestamp)}
                className=""
              />
            </div>
          </li>
        ))}
      </ul>

      {/* Never a silent omission: a member whose only read scans are sealed
          would otherwise read this list as "nobody has looked at me". */}
      {sealed && (
        <p className="mt-5 m-0 font-display text-[13px] leading-relaxed text-bone-dim">
          {COPY.FEEDBACK_RECEIVED_SEALED_NOTE}
        </p>
      )}

      {hasMore && (
        <div className="mt-6 flex justify-center">
          <BoneButton onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? COPY.ANALYZING_LABEL : COPY.VAULT_LOAD_MORE_CTA}
          </BoneButton>
        </div>
      )}
    </div>
  );
}
